import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export interface UtmData {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  gclid: string | null;
  landing_page: string | null;
}

// Capture UTM parameters and store in sessionStorage
// This preserves attribution across SPA navigation
export const captureUtmParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  
  // Check if there are tracking params in the current URL
  const hasTrackingParams = 
    urlParams.has('utm_source') || 
    urlParams.has('utm_medium') || 
    urlParams.has('utm_campaign') ||
    urlParams.has('gclid');
  
  if (hasTrackingParams) {
    const utmData: UtmData = {
      utm_source: urlParams.get('utm_source'),
      utm_medium: urlParams.get('utm_medium'),
      utm_campaign: urlParams.get('utm_campaign'),
      utm_term: urlParams.get('utm_term'),
      utm_content: urlParams.get('utm_content'),
      gclid: urlParams.get('gclid'),
      landing_page: window.location.pathname,
    };
    
    // Store in sessionStorage (persists for the session, survives SPA navigation)
    sessionStorage.setItem('utm_data', JSON.stringify(utmData));
    console.log('UTM data captured:', utmData);
  }
};

// Get stored UTM data - always returns the captured data even after navigation
export const getUtmData = (): UtmData => {
  const stored = sessionStorage.getItem('utm_data');
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return getEmptyUtmData();
    }
  }
  return getEmptyUtmData();
};

const getEmptyUtmData = (): UtmData => ({
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_term: null,
  utm_content: null,
  gclid: null,
  landing_page: null,
});

// Hook to capture UTM params on initial load and preserve them
export const useUtmTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Always check for new UTM params on URL changes
    // This handles cases where UTM params appear later in the session
    captureUtmParams();
  }, [location.search]); // Re-run when query params change

  // Initial capture on mount
  useEffect(() => {
    captureUtmParams();
  }, []);
};

// Google Ads Tracking Template
// Use this in your Google Ads campaign URL suffix or tracking template:
// 
// Final URL suffix:
// utm_source=google&utm_medium=cpc&utm_campaign={campaignid}&utm_content={adgroupid}&utm_term={keyword}&gclid={gclid}
//
// Or use ValueTrack parameters in tracking template:
// {lpurl}?utm_source=google&utm_medium=cpc&utm_campaign={campaignid}&utm_content={adgroupid}&utm_term={keyword}&gclid={gclid}
